import { createAuthHeaders, createGetHeaders } from './auth-utils'

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: any
  headers?: Record<string, string>
  auth?: {
    restaurantId: string
    key: string
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: Response
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Unified API service with consistent error handling and auth
 */
export class ApiService {
  private static async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorMessage = `API request failed: ${response.status} ${response.statusText}`
      console.error(errorMessage)
      throw new ApiError(errorMessage, response.status, response)
    }

    if (response.status === 204 || response.status === 205) {
      return null as T
    }

    const contentType = response.headers.get('content-type') || ''
    const isJson = contentType.includes('application/json')

    try {
      const text = await response.text()
      if (!text) {
        return null as T
      }

      if (isJson) {
        return JSON.parse(text)
      }

      return text as T
    } catch (error) {
      console.error('Failed to parse response:', error)
      throw new ApiError(`Failed to parse ${isJson ? 'JSON' : 'text'} response`)
    }
  }

  static async request<T>(url: string, options: ApiRequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      body,
      headers = {},
      auth
    } = options

    let finalHeaders = { ...headers }

    if (auth) {
      const authHeaders = method === 'GET' 
        ? createGetHeaders(auth.restaurantId, auth.key)
        : createAuthHeaders(auth.restaurantId, auth.key)
      finalHeaders = { ...finalHeaders, ...authHeaders }
    }

    const requestOptions: RequestInit = {
      method,
      headers: finalHeaders
    }

    if (body && method !== 'GET') {
      requestOptions.headers = {
        ...requestOptions.headers,
        'Content-Type': 'application/json'
      }
      requestOptions.body = typeof body === 'string' ? body : JSON.stringify(body)
    }

    try {
      const response = await fetch(url, requestOptions)
      return await this.handleResponse<T>(response)
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      
      const errorMessage = `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`
      console.error(errorMessage, error)
      throw new ApiError(errorMessage)
    }
  }

  static async get<T>(url: string, auth?: { restaurantId: string, key: string }): Promise<T> {
    return this.request<T>(url, { method: 'GET', auth })
  }

  static async post<T>(url: string, body: any, auth?: { restaurantId: string, key: string }): Promise<T> {
    return this.request<T>(url, { method: 'POST', body, auth })
  }

  static async put<T>(url: string, body: any, auth?: { restaurantId: string, key: string }): Promise<T> {
    return this.request<T>(url, { method: 'PUT', body, auth })
  }

  static async delete<T>(url: string, auth?: { restaurantId: string, key: string }): Promise<T> {
    return this.request<T>(url, { method: 'DELETE', auth })
  }
}
